import {
  definitions as CustomerCommunicationDefinitions,
  paths as CustomerCommunicationPaths,
} from '@luxuryescapes/contract-customer-communication';

import { json_headers, request } from '~/services/common';

import { makeCustomerCommunicationV1URL } from './customerCommunicationRequestUtils';

type CustomSchedulePostContract = CustomerCommunicationPaths['/api/customer-communication/v1/custom-schedules']['post'];
type CustomSectionOfferType = CustomerCommunicationDefinitions['offerSectionSchema'];
type CustomSectionFlightType = CustomerCommunicationDefinitions['flightSectionSchema'];
type CustomSectionBannerType = CustomerCommunicationDefinitions['bannerSectionSchema'];
type CustomSectionDreamArticleType = CustomerCommunicationDefinitions['dreamArticleSectionSchema'];
type CustomSectionDestinationType = CustomerCommunicationDefinitions['destinationSectionSchema'];
type CustomSectionCtaType = CustomerCommunicationDefinitions['ctaSectionSchema'];
type CustomSectionBrazeBannerType = CustomerCommunicationDefinitions['brazeBannerSectionSchema'];
type CustomSectionCustomOfferType = CustomerCommunicationDefinitions['customOfferSectionSchema'];
type CustomSectionHeaderBlockType = CustomerCommunicationDefinitions['headerBlockSectionSchema'];

function mapCustomSchedule(
  external: CustomSchedulePostContract['responses']['200']['content']['application/json']['result'],
): CustomerCommunication.CustomSchedulePostPayload {
  return {
    id: external.customScheduleId,
  };
}

function mapNewExternalCustomSchedule(
  internal: CustomerCommunication.UnsavedCustomSchedule,
): CustomSchedulePostContract['requestBody']['content']['application/json'] {
  const pushSchedules = internal.pushSchedules?.map((pushSchedule) => {
    return {
      title: pushSchedule.title,
      subtitle: pushSchedule.subtitle,
      message: pushSchedule.message,
      link: pushSchedule.link,
      imageId: pushSchedule.imageId,
      scheduleType: pushSchedule.scheduleType,
      status: 'DRAFT' as const,
    };
  });

  const customSections = [];
  for (const section of internal.customSections) {
    switch (section.type) {
      case 'OFFER': {
        const sectionPayload: CustomSectionOfferType = {
          type: section.type,
          position: section.position,
          heading: section.heading || undefined,
          offerIds: section.offerIds,
        };
        customSections.push(sectionPayload);
        break;
      }
      case 'FLIGHT': {
        const sectionPayload: CustomSectionFlightType = {
          type: section.type,
          position: section.position,
          heading: section.heading || undefined,
          flights: section.flights.map((flight) => {
            return {
              departureCity: flight.departingCity,
              arrivalCity: flight.arrivalCity,
              pricePerPerson: flight.pricePerPerson,
              airlineLogoImageId: flight.imageId || undefined,
              flightUrl: flight.url,
              tripType: flight.tripType,
            };
          }),
        };
        customSections.push(sectionPayload);
        break;
      }
      case 'BANNER': {
        const sectionPayload: CustomSectionBannerType = {
          type: section.type,
          position: section.position,
          bannerName: section.bannerName,
          bannerUrl: section.bannerUrl,
          desktopImageId: section.desktopImageId,
          mobileImageId: section.mobileImageId,
          subtext: section.subtext,
          ctaLabel: section.ctaLabel,
          palette: section.palette,
          tags: section.tags,
        };
        customSections.push(sectionPayload);
        break;
      }
      case 'DREAM_ARTICLE': {
        const sectionPayload: CustomSectionDreamArticleType = {
          destination: section.destination,
          type: section.type,
          position: section.position,
          heading: section.heading,
          subheading: section.subheading,
          articleUrl: section.articleUrl,
          desktopImageId: section.desktopImageId,
          mobileImageId: section.mobileImageId,
        };
        customSections.push(sectionPayload);
        break;
      }
      case 'DESTINATION': {
        const sectionPayload: CustomSectionDestinationType = {
          type: section.type,
          position: section.position,
          heading: section.heading || undefined,
          destinations: section.destinations.map((destination) => {
            return {
              name: destination.name,
              imageId: destination.imageId,
              redirectUrl: destination.redirectUrl,
            };
          }),
        };
        customSections.push(sectionPayload);
        break;
      }
      case 'CALL_TO_ACTION': {
        const sectionPayload: CustomSectionCtaType = {
          type: section.type,
          position: section.position,
          ctaText: section.text,
          ctaUrl: section.url,
        };
        customSections.push(sectionPayload);
        break;
      }
      case 'BRAZE_BANNER': {
        const sectionPayload: CustomSectionBrazeBannerType = {
          type: section.type,
          position: section.position,
          brazeBannerId: section.brazeBannerId,
        };
        customSections.push(sectionPayload);
        break;
      }
      case 'CUSTOM_OFFER': {
        const sectionPayload: CustomSectionCustomOfferType = {
          type: section.type,
          position: section.position,
          offerId: section.offerId,
          specialOffer: section.specialOffer,
          heading: section.heading ?? '',
          subheading: section.subheading ?? '',
          descriptionHeading: section.descriptionHeading ?? '',
          descriptionMarkdown: section.descriptionMarkdown ?? '',
        };
        customSections.push(sectionPayload);
        break;
      }
      case 'HEADER_BLOCK': {
        const sectionPayload: CustomSectionHeaderBlockType = {
          type: section.type,
          position: section.position,
          heading: section.heading ?? '',
          subheading: section.subheading ?? '',
        };
        customSections.push(sectionPayload);
        break;
      }
      case 'CUSTOM_TEXT': {
        customSections.push({
          type: section.type,
          position: section.position,
          text: section.text,
        });
        break;
      }
    }
  }

  type Body = CustomSchedulePostContract['requestBody']['content']['application/json'];
  return {
    identifier: {
      brand: internal.brandId as Body['identifier']['brand'],
      country: internal.countryId as Body['identifier']['country'],
      countryGroup: internal.countryGroupId as Body['identifier']['countryGroup'],
      state: internal.stateId as Body['identifier']['state'],
      segment: internal.segmentId as Body['identifier']['segment'],
      city: internal.cityId as Body['identifier']['city'],
      cadence: internal.cadenceId as Body['identifier']['cadence'],
      loyalty: internal.membershipId as Body['identifier']['loyalty'],
    },
    sendDate: internal.sendDate.format('YYYY-MM-DD'),
    type: internal.type as Body['type'],
    holidayType: (internal.type === 'HOLIDAY_TYPE' ? internal.holidayType : undefined) as Body['holidayType'],
    destination: (internal.type === 'DESTINATION' ? internal.destination : undefined) as Body['destination'],
    emailSubjectLine: internal.emailSubjectLine,
    emailPreheader: internal.emailPreheader,
    // introSection: internal.introSection,
    introSection: {
      headerDesktopImage: internal.introSection.headerDesktopImage || undefined,
      headerUrl: internal.introSection.headerUrl || undefined,
      headerMobileImage: internal.introSection.headerMobileImage || undefined,
      introHeading: internal.introSection.introHeading || undefined,
      introCopy: internal.introSection.introCopy || undefined,
      introCta: internal.introSection.introCta || undefined,
      introCtaUrl: internal.introSection.introCtaUrl || undefined,
    },
    customSections: customSections,
    status: 'DRAFT',
    pushSchedules,
  };
}

async function requestPostCustomSchedule(unsavedSchedule: CustomerCommunication.UnsavedCustomSchedule) {
  if (!unsavedSchedule) {
    throw 'missing unsavedSchedule';
  }

  const url = makeCustomerCommunicationV1URL('/custom-schedules');

  return request(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: json_headers,
    body: JSON.stringify(mapNewExternalCustomSchedule(unsavedSchedule)),
  })
    .then((response: CustomSchedulePostContract['responses']['200']['content']['application/json']) => {
      return mapCustomSchedule(response.result);
    })
    .catch((error) => {
      throw JSON.stringify(error, null, 2);
    });
}

export default requestPostCustomSchedule;
